<template>
    <div v-if="hasFieldToDisplay()" id="localisation">
        <div class="tabulation-title">
            <h5>
                <v-icon class="blue--text text--darken-2">label</v-icon>
                Localisation
            </h5>
        </div>

        <div class="form-container mr-auto ml-auto mt-3">
            <v-text-field :class="{'no-detail': suggestedAddress !== null}" v-if="hasField('ADDRESS')"
                          autocomplete="new-address" v-model="provider.localisation.address" label="Adresse" outlined
                          :disabled="readOnly">
            </v-text-field>

            <div v-if="suggestedAddress !== null && suggestedAddress !== undefined && suggestedAddress !== ''">
                <div class="suggestion-container" style="width: 100% !important; margin-bottom: 30px">
                    <div class="d-flex justify-content-between">
                        <div>
                            <b>Suggestion proposée - Adresse</b>
                        </div>

                        <div class="d-flex mb-auto ml-md-0 ml-5 mt-md-auto mt-3">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn small color="success" class="mr-3" icon v-on="on" dark @click="setAddress()">
                                        <v-icon>check</v-icon>
                                    </v-btn>
                                </template>
                                <div>Accepter</div>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" small dark icon color="error" @click="removeAddressSuggestion()">
                                        <v-icon>close</v-icon>
                                    </v-btn>
                                </template>
                                <div>Refuser</div>
                            </v-tooltip>
                        </div>
                    </div>

                    <div class="mt-3">
                        <div style="color: gray">
                            <i>Information présente sur Google</i>
                        </div>
                        <v-text-field :value="suggestedAddress" outlined disabled></v-text-field>
                    </div>
                </div>
            </div>

            <v-text-field :class="{'col-lg-12': !hasField('ADDRESS'),
            'input-error': provider.localisation.complement && provider.localisation.complement.length > 255}"
                          v-if="hasField('COMPLEMENT_ADDRESS')"
                          autocomplete="new-address-complement" v-model="provider.localisation.complement"
                          label="Complément d'adresse" outlined @change="checkAddress2Length"
                          :disabled="readOnly">
            </v-text-field>

            <v-text-field :class="{'col-lg-12': !hasField('ADDRESS')}" v-if="hasField('ADDRESS3')"
                          autocomplete="new-address-3" v-model="provider.localisation.address3"
                          label="Adresse 3" outlined
                          :disabled="readOnly">
            </v-text-field>

            <v-autocomplete v-if="hasField('CITY')" v-model="provider.localisation.commune"
                            :class="{'no-detail': suggestedCommune !== null}" :items="communes" label="Ville"
                            item-text="name"
                            return-object :loading="loadingCommunes" :search-input.sync="searchCommunes"
                            no-data-text="Saisissez pour sélectionner une ville" outlined
                            :disabled="readOnly">
            </v-autocomplete>

            <v-text-field v-if="hasField('POSTAL_CODE')"
                          autocomplete="new-address-3" v-model="provider.localisation.postal_code"
                          label="Code postal" outlined
                          :disabled="readOnly">
            </v-text-field>

            <div v-if="suggestedCommune !== null && suggestedCommune !== undefined">
                <div class="suggestion-container" style="width: 100% !important; margin-bottom: 30px">
                    <div class="d-flex justify-content-between">
                        <div>
                            <b>Suggestion proposée - Commune</b>
                        </div>

                        <div class="d-flex mb-auto ml-md-0 ml-5 mt-md-auto mt-3">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn small color="success" class="mr-3" icon v-on="on" dark @click="setCommune()">
                                        <v-icon>check</v-icon>
                                    </v-btn>
                                </template>
                                <div>Accepter</div>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" small dark icon color="error" @click="removeCommuneSuggestion()">
                                        <v-icon>close</v-icon>
                                    </v-btn>
                                </template>
                                <div>Refuser</div>
                            </v-tooltip>
                        </div>
                    </div>

                    <div class="mt-3">
                        <div style="color: gray">
                            <i>Information présente sur Google</i>
                        </div>
                        <v-text-field :value="suggestedCommune.name" outlined disabled></v-text-field>
                    </div>
                </div>
            </div>

            <div v-if="marker !== null && hasField('GPS') && gpsCardReady"
                 style="flex: 1 1 0" class="mt-2 mb-5">
                <l-map style="height: 350px; border-top-right-radius: 8px; border-top-left-radius: 8px; z-index: 1"
                       :options="{
                         dragging: false,
                         zoomControl: false,
                         doubleClickZoom: false
                    }" :zoom="16" :center="marker"
                       @ready="setMarker">
                    <l-tile-layer :url="url" :attribution="attribution" @ready="setMarker" />
                    <l-marker :lat-lng="marker"></l-marker>
                </l-map>
                <div class="map-footer d-flex justify-content-between">
                    <div class="mt-auto mb-auto">
                        <div class="d-flex" v-if="provider.localisation.latitude === null">
                            <v-icon color="warning" class="mr-2">warning</v-icon>
                            <p class="mb-0" style="color: white">Vos coordonnées GPS ne sont pas exactes</p>
                        </div>
                    </div>
                    <v-btn :style="'background-color: #1398b5 !important'"
                           class="default-button text-none mt-auto mb-auto" @click="openGpsModal"
                           v-if="!readOnly">
                        Modifier l'emplacement
                    </v-btn>
                </div>
            </div>

            <v-autocomplete v-if="hasFamilyElement('Environnement')" :value="environnements"
                            :items="getFamilyByCode('Environnement')" chips
                            @change="updateOrDeleteElement($event, 'environnements')" deletable-chips
                            label="Environnement"
                            item-text="label" return-object multiple outlined
                            :disabled="readOnly">
            </v-autocomplete>

            <v-text-field v-if="hasField('COMPLEMENT_LOCALISATION')" v-model="provider.localisation.access"
                          label="Complément de localisation" autocomplete="new-address-complement-loc" outlined
                          :disabled="readOnly">
            </v-text-field>

            <v-text-field v-if="hasField('PROVINCE')"
                          autocomplete="new-address-province" v-model="provider.localisation.province"
                          label="Province" outlined
                          :disabled="readOnly">
            </v-text-field>
        </div>

        <v-dialog v-model="gpsModal" persistent fullscreen hide-overlay transition="dialog-bottom-transition">
            <v-toolbar dark color="primary">
                <v-toolbar-title>Modification de vos coordonnées GPS</v-toolbar-title>
            </v-toolbar>

            <div style="height: calc(100% - 64px); background-color: white">
                <div style="height: calc(100% - 68px)">
                    <l-map
                      style="height: 100%; width: 100%; border-top-right-radius: 8px; border-top-left-radius: 8px; z-index: 1"
                      :zoom="16" v-if="loadedModal" :center="marker">
                        <l-tile-layer :url="url" :attribution="attribution" />
                        <l-marker :lat-lng="marker" draggable @dragend="updateLatLng"></l-marker>
                    </l-map>
                </div>

                <div class="d-flex justify-content-between p-3" style="background-color: #414a65">
                    <div class="d-flex">
                        <v-btn class="default-button mr-3" color="error" @click="cancelUpdateGps">Annuler</v-btn>
                        <v-btn style="color: white" color="#1398b5" @click="resetGps">Réinitialiser</v-btn>
                    </div>

                    <div class="mt-auto mb-auto" style="color: white">
                        Cliquez et restez appuyé sur l'icône au centre de la carte pour le déplacer sur votre
                        établissement.
                    </div>

                    <v-btn style="color: white" color="#1398b5" @click="gpsModal = false">Valider</v-btn>
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"
import _ from "lodash"
import { latLng } from "leaflet"

export default {
    name: "Localisation",
    props: ["readOnly"],
    data() {
        return {
            environnements: [],
            communes: [],
            loadingCommunes: false,
            searchCommunes: null,
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: "&copy; <a href=\"http://osm.org/copyright\">OpenStreetMap</a> contributors",
            marker: null,
            tmpMarker: null,
            originaMarker: null,
            cityGps: false,
            gpsModal: false,
            loadedModal: false,
            gpsCardReady: false,
        }
    },
    computed: {
        ...mapState("web_form", ["provider", "disabled", "suggestedAddress", "suggestedCommune"]),
        ...mapGetters("web_form", ["hasField", "hasFamilyElement", "getFamilyByCode", "getElementsByFamilyCode"]),
        ...mapState("account", ["account"]),
    },
    methods: {
        ...mapMutations("web_form", [
            "addElement",
            "removeElement",
            "updateProvider",
            "setSuggestedAddress",
            "setSuggestedCommune",
            'setAddress2TooLong',
        ]),
        hasFieldToDisplay() {
            return this.hasField("ADDRESS") ||
              this.hasField("COMPLEMENT_ADDRESS") ||
              this.hasField("CITY") ||
              this.hasField("COMPLEMENT_LOCALISATION") ||
              this.hasField("ADDRESS3") ||
              this.hasFamilyElement("Environnement") ||
              this.hasField("POSTAL_CODE") ||
              this.hasField("PROVINCE")
        },
        addNewElement(currentElements, newElements) {
            let element = newElements
            const previousValue = JSON.parse(JSON.stringify(currentElements))

            if (Array.isArray(newElements)) {
                let diff = newElements.filter(_element => !previousValue.includes(_element)) || []

                if (diff.length) {
                    element = diff[diff.length - 1]
                }
            } else if (previousValue) {
                this.removeElement(previousValue)
            }

            this.addElement(element)
        },
        deleteElement(currentElements, newElements) {
            const previousValue = JSON.parse(JSON.stringify(currentElements))

            const diff = previousValue.filter(element => {
                return !newElements.map(elt => elt.id).includes(element.id)
            }) || []

            let element = newElements

            if (diff.length) {
                element = diff[diff.length - 1]
            }

            this.removeElement(element)
        },
        updateOrDeleteElement(newElements, property) {
            let currentElements = this[property]

            if (currentElements.length > newElements.length) {  // Cas suppression
                this.deleteElement(currentElements, newElements)
            } else { // Cas Ajout
                this.addNewElement(currentElements, newElements)
            }

            this[property] = newElements
        },
        getCommunes: _.debounce(function() {
            this.loadingCommunes = true

            let payload = {
                search: this.searchCommunes,
                providerId: this.provider.provider.id,
            }

            this.$http.post("resource/communes", payload).then(res => {
                this.communes = res.body.data.communes

                this.loadingCommunes = false
            })
        }, 500),
        setAddress() {
            this.provider.localisation.address = this.suggestedAddress

            this.setSuggestedAddress(null)
        },
        setCommune() {
            this.communes.push(this.suggestedCommune)

            this.provider.localisation.commune = this.suggestedCommune

            this.searchCommunes = this.provider.localisation.commune.name

            this.setSuggestedCommune(null)
        },
        removeAddressSuggestion() {
            this.setSuggestedAddress(null)
        },
        removeCommuneSuggestion() {
            this.setSuggestedCommune(null)
        },
        openGpsModal() {
            this.tmpMarker = JSON.parse(JSON.stringify(this.marker))
            this.gpsModal = true

            setTimeout(() => {
                this.loadedModal = true
            }, 300)
        },
        updateLatLng(event) {
            let marker = event.target
            let latLng = marker.getLatLng()

            this.provider.localisation.latitude = latLng.lat
            this.provider.localisation.longitude = latLng.lng

            this.marker = latLng
        },
        resetGps() {
            this.marker = this.originaMarker

            if (!this.cityGps) {
                // reset la valeur de base des lat/lng du provider
                this.provider.localisation.latitude = this.marker.lat
                this.provider.localisation.longitude = this.marker.lng
            } else {
                // si on a des coordonnees de la commune c'est que celle de la localisation étaient nulle à la base
                this.provider.localisation.latitude = null
                this.provider.localisation.longitude = null
            }
        },
        setMarker() {
            if (this.provider.localisation !== null && this.provider.localisation !== undefined) {
                if (this.provider.localisation.latitude !== null && this.provider.localisation.latitude !== undefined) {
                    this.marker = latLng(this.provider.localisation.latitude, this.provider.localisation.longitude)
                } else {
                    this.marker = latLng(this.provider.localisation.commune.latitude, this.provider.localisation.commune.longitude)
                    this.cityGps = true
                }

                this.originaMarker = JSON.parse(JSON.stringify(this.marker))
            }
        },
        cancelUpdateGps() {
            this.marker = this.tmpMarker
            this.gpsModal = false
        },
        checkAddress2Length() {
            if (this.account.tis_type.code === "APIDAE" && this.provider.localisation.complement) {
                this.setAddress2TooLong(this.provider.localisation.complement.length > 255)
            }
        },
    },
    watch: {
        provider: {
            handler: _.debounce(function() {
                this.updateProvider(this.provider)
            }, 200),
            deep: true,
        },
        searchCommunes() {
            this.getCommunes()
        },
    },
    mounted() {
        if (this.provider.localisation.commune !== undefined && this.provider.localisation.commune !== null) {
            this.communes.push(this.provider.localisation.commune)
            this.searchCommunes = this.provider.localisation.commune.name
        }

        this.setMarker()
    },
    created() {
        this.environnements = this.getElementsByFamilyCode("Environnement")

        setTimeout(() => {
            this.gpsCardReady = true
        }, 1500)
    },
}
</script>